import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import Squircle from 'design-system/Squircle';
import StatusBar from 'design-system/StatusBar';
import StatusIcon from 'design-system/StatusIcon';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { AppView, Button, ButtonGroup, CurrencyAmountText, rem, SCROLL_INDICATOR_INSETS, Spacer } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import ProgressCard from '../components/InvestOnboardingFunnel/ProgressCard';
import { useStockMeta } from '../hooks/selectors';
import { formatDateInProgressTimeline } from '../utils';
import { isWeb } from '../../../constants';
const RELATIVE_DAYS = ['Today', 'Tomorrow', 'Yesterday'];
const formatWithOnPrefix = (prefix, date) => {
    const formattedDate = formatDateInProgressTimeline(date);
    return RELATIVE_DAYS.includes(formattedDate) ? `${prefix} ${formattedDate}` : `${prefix} on ${formattedDate}`;
};
const strings = {
    pendingInvestment: 'Initial investment ',
    directDebitInitiated: 'Direct Debit initiated',
    directDebitInitiatedDescription: 'We’ve sent the Direct Debit request to your bank.',
    collectingFromBank: 'Collecting from bank',
    collectingFromBankFailed: 'We couldn’t collect the money',
    collectingFromBankDescription: 'We’re collecting the payment. This usually takes 3-5 working days.',
    collectingFromBankDescriptionFailed: 'This is often down to things like insufficient funds or a cancelled Direct Debit mandate. It’s best to check with your bank directly, then feel free to try again',
    investingYourMoney: 'Investing your money',
    investingYourMoneyDescription: 'We’ll confirm the final price once the purchase completes',
    amount: 'Amount',
    close: 'Close',
};
const DepositAndBuyAssetTimeline = withAnimatedNavigationHeader(({ scrollY, navigation, route }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { assetId, amount, timestamp, status, fundingMethod, dateOfCollection, estimatedDateOfCollection, dateOfInvesting, estimatedDateOfInvesting, } = route.params;
    const stockMeta = useStockMeta(assetId);
    const assetType = useAppSelector((store) => store.invest.caches[assetId]?.type);
    const isFailed = status === 'failed';
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => (<NavigationCloseButton inWebHeader onPress={() => {
                    navigation.pop();
                }}/>),
            headerBackVisible: false,
        });
    }, []);
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = handle.contentOffset.y;
    });
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, undefined, paddingHorizontalStyle);
    const handleClose = useCallback(() => {
        navigation.goBack();
    }, []);
    const collectingTimeInfoFormatted = useMemo(() => {
        if (status === 'pending') {
            if (dateOfCollection) {
                return formatDateInProgressTimeline(dateOfCollection);
            }
            if (estimatedDateOfCollection) {
                return formatWithOnPrefix('Estimated', estimatedDateOfCollection);
            }
        }
        return undefined;
    }, [dateOfCollection, estimatedDateOfCollection, status]);
    const investingTimeInfoFormatted = useMemo(() => {
        if (status === 'pending') {
            if (dateOfInvesting) {
                return formatDateInProgressTimeline(dateOfInvesting);
            }
            if (estimatedDateOfInvesting) {
                return formatWithOnPrefix('Expected', estimatedDateOfInvesting);
            }
        }
        return undefined;
    }, [dateOfInvesting, estimatedDateOfInvesting, status]);
    const content = useMemo(() => {
        if (fundingMethod === 'openBankingDeposit') {
            return (<>
            <ProgressCard lightBg completed noTextDecoration alwaysShowTimeline alwaysShowChildren title="Buy requested" timeInfo={formatDateInProgressTimeline(timestamp)}/>
            <ProgressCard lightBg lastItem isActiveStep completed={false} noTextDecoration isFailure={isFailed} title={strings.investingYourMoney} timeInfo={investingTimeInfoFormatted}>
              <Text TextThin-14 Secondary>
                {strings.investingYourMoneyDescription}
              </Text>
            </ProgressCard>
          </>);
        }
        return (<>
          <ProgressCard lightBg completed noTextDecoration alwaysShowTimeline alwaysShowChildren title={strings.directDebitInitiated} timeInfo={formatDateInProgressTimeline(timestamp)}>
            <Text TextThin-14 Secondary>
              {strings.directDebitInitiatedDescription}
            </Text>
          </ProgressCard>
          <ProgressCard lightBg isActiveStep noTextDecoration isFailure={isFailed} lastItem={isFailed} completed={!!dateOfCollection} timeInfo={collectingTimeInfoFormatted} title={isFailed ? strings.collectingFromBankFailed : strings.collectingFromBank}>
            <Text TextThin-14 Secondary>
              {isFailed ? strings.collectingFromBankDescriptionFailed : strings.collectingFromBankDescription}
            </Text>
          </ProgressCard>
          {!isFailed && (<ProgressCard lightBg lastItem completed={false} noTextDecoration isActiveStep={!!dateOfCollection} title={strings.investingYourMoney} timeInfo={investingTimeInfoFormatted}>
              <Text TextThin-14 Secondary>
                {strings.investingYourMoneyDescription}
              </Text>
            </ProgressCard>)}
        </>);
    }, [collectingTimeInfoFormatted, dateOfCollection, fundingMethod, investingTimeInfoFormatted, isFailed, timestamp]);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
          <Spacer h={24}/>
          <View style={styles.amountInfo}>
            <View>
              <Logo R64 withOutline uri={stockMeta?.iconUrl} resizeMode="contain"/>
              <View style={styles.statusIconContainer}>
                <Squircle width={rem(24)} height={rem(24)} color={colors.cards.onDark}>
                  <StatusIcon state={!isFailed ? 'in-progress' : 'cancelled-red'} height={20} width={20}/>
                </Squircle>
              </View>
            </View>
            <Spacer h={24}/>
            <View style={styles.orderTypeContainer}>
              <Text Text-14 Secondary centered>
                Buy {stockMeta?.name || assetId}
                {assetType ? ` ${assetType}` : ''}
              </Text>
            </View>
            <CurrencyAmountText size="XL" value={{ amount: amount.value, currency: amount.currency }}/>
          </View>
          <Spacer h={24}/>
          {content}
          <Spacer h={24}/>
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button title={strings.close} brand onPress={handleClose}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    title: ``,
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    amountInfo: {
        alignItems: 'center',
    },
    statusIconContainer: {
        right: 0,
        bottom: 0,
        position: 'absolute',
    },
    orderTypeContainer: {
        paddingHorizontal: rem(32),
    },
}));
export default DepositAndBuyAssetTimeline;
